// src/components/farmers/Farmers.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./farmers.css";
import { useCustomerRepo } from "../../repos/AppRepoProvider";
import { api as http, extractJsonObject } from "../../services/api";
import { getSession } from "../../services/auth";

// ✅ list endpoint (same as provider)
const API_CUSTOMERS = "getcustomersmd.php";

// ⚠️ Change this to your real save endpoint
const API_SAVE_CUSTOMER = "savecustomermd.php";

// -------- helpers --------
const normStr = (v) => (v == null ? "" : String(v).trim());
const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

function extractCustomerArray(body) {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

function safeNumOrBig(s) {
  const x = parseInt(String(s || "").trim(), 10);
  return Number.isFinite(x) ? x : Number.MAX_SAFE_INTEGER;
}

function statusLabel(rec) {
  const s = normStr(rec.status ?? rec.active ?? rec.is_active ?? rec.isActive);
  if (!s) return "Inactive";
  const x = s.toLowerCase();
  if (x === "active" || x === "1" || x === "true" || x === "yes") return "Active";
  if (x === "inactive" || x === "0" || x === "false" || x === "no") return "Inactive";
  if (s === "Active" || s === "Inactive") return s;
  return s;
}

/**
 * Android resolves milk type using strings:
 * - Cow
 * - Buffalo
 * - Both (Cow + Buffalo) (sometimes localized)
 *
 * We normalize many possible backend/localized variants to:
 *  "Cow" | "Buffalo" | "Cow + Buffalo"
 */
function normalizeMilkType(v) {
  const raw = normStr(v);
  const s = raw.toLowerCase();

  if (!s) return "";

  // common English variants
  if (s === "cow" || s === "c") return "Cow";
  if (s === "buffalo" || s === "buf" || s === "b" || s.includes("buff")) return "Buffalo";

  // both / combined variants
  if (
    s === "both" ||
    s.includes("cow + buffalo") ||
    (s.includes("cow") && s.includes("buff")) ||
    s.includes("cow&buff") ||
    s.includes("cow and buffalo") ||
    s.includes("cow buffalo")
  ) {
    return "Cow + Buffalo";
  }

  // Marathi-ish heuristics (best-effort)
  // गाय = cow, म्हैस = buffalo, दोन्ही = both
  if (s.includes("दोन्ह") || (s.includes("गाय") && s.includes("म्हैस"))) return "Cow + Buffalo";
  if (s.includes("गाय")) return "Cow";
  if (s.includes("म्हैस")) return "Buffalo";

  // if already proper UI string
  if (raw === "Cow" || raw === "Buffalo" || raw === "Cow + Buffalo") return raw;

  // fallback: return original so we can still display it
  return raw;
}

function splitName(fullName) {
  const s = normStr(fullName);
  if (!s) return { first: "", last: "" };
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
}

/**
 * Extra info (कपात / देय) can come as:
 * - raw.kapat (array) / raw.dey (array)
 * - raw.kapat_json (stringified json array) / raw.dey_json
 * - raw.kapat / raw.dey as stringified json
 */
function safeParseArr(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;

  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return [];
    try {
      const j = JSON.parse(t);
      if (Array.isArray(j)) return j;
      if (j && Array.isArray(j.data)) return j.data;
    } catch {}
  }

  // sometimes backend sends object with data:[]
  if (typeof v === "object" && v && Array.isArray(v.data)) return v.data;

  return [];
}

function normalizeExtraItems(raw) {
  const kapatArr =
    safeParseArr(raw?.kapat) ||
    safeParseArr(raw?.kapat_json) ||
    safeParseArr(raw?.kapatJson);

  const deyArr =
    safeParseArr(raw?.dey) ||
    safeParseArr(raw?.dey_json) ||
    safeParseArr(raw?.deyJson);

  const out = [];

  if (kapatArr.length) out.push({ _hdr: true, title: "Extra Information" });

  // Android uses name + amt + sr
  for (const o of kapatArr) {
    const sr = n(o?.sr ?? o?.id ?? 0);
    const label = normStr(o?.name ?? o?.label ?? "");
    const amt = normStr(o?.amt ?? o?.value ?? "0");
    out.push({
      kind: "kapat",
      sr,
      label: label || `Kapat ${sr || ""}`.trim(),
      amt,
    });
  }

  for (const o of deyArr) {
    const sr = n(o?.sr ?? o?.id ?? 0);
    const label = normStr(o?.name ?? o?.label ?? "");
    const amt = normStr(o?.amt ?? o?.value ?? "0");
    out.push({
      kind: "dey",
      sr,
      label: label || `Dey ${sr || ""}`.trim(),
      amt,
    });
  }

  return out;
}

function normalizeFarmerRow(rec) {
  const kno = normStr(rec.kno ?? rec.farmer_code ?? rec.code ?? rec.sr ?? rec.no);
  const cname = normStr(rec.cname ?? rec.farmer_name ?? rec.name ?? "");
  const enname = normStr(rec.enname ?? rec.name_english ?? rec.english_name ?? "");
  const mobno = normStr(rec.mobno ?? rec.mobileuser ?? rec.phone ?? rec.phone_number ?? "");
  const type = normalizeMilkType(rec.type ?? rec.milk_type ?? rec.milktype ?? "");
  const st = statusLabel(rec);

  return {
    raw: rec,
    kno,
    cname,
    enname,
    mobno,
    type,
    status: st,
  };
}

export default function Farmers() {
  const customersRepo = useCustomerRepo();

  const session = getSession();
  const mainuser =
    session?.mobno || session?.user?.mobno || session?.user?.username || "";

  // view: "list" | "add" | "details"
  const [view, setView] = useState("list");
  const [editMode, setEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // list state
  const [q, setQ] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // form state
  const [fCode, setFCode] = useState("");
  const [fFirst, setFFirst] = useState("");
  const [fLast, setFLast] = useState("");
  const [fEn, setFEn] = useState("");
  const [fPhone, setFPhone] = useState("");
  const [fMilkType, setFMilkType] = useState("");
  const [fStatus, setFStatus] = useState("Active");

  // dynamic extra info (kapat/dey)
  const [extraItems, setExtraItems] = useState([]); // [{kind,sr,label,amt}] or header marker
  const initialFormRef = useRef(null);

  // ---------------- load list ----------------
  const loadFromServer = useCallback(async () => {
    if (!mainuser) return;
    setErr("");
    setLoading(true);
    try {
      const resp = await http.get(API_CUSTOMERS, {
        params: { mainuser, nocache: Date.now() },
        validateStatus: (s) => s >= 200 && s < 300,
      });

      const body =
        typeof resp.data === "string"
          ? JSON.parse(extractJsonObject(resp.data))
          : resp.data;

      const arr = extractCustomerArray(body);
      const normalized = arr.map(normalizeFarmerRow);

      normalized.sort((a, b) => {
        const ka = safeNumOrBig(a.kno);
        const kb = safeNumOrBig(b.kno);
        if (ka !== kb) return ka - kb;
        return a.cname.localeCompare(b.cname);
      });

      setRows(normalized);
      setPage(1);
    } catch (e) {
      setErr(e?.message || "Failed to load farmers.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [mainuser]);

  useEffect(() => {
    // quick warm UI from repo cache if available
    const cached = customersRepo?.getAll ? customersRepo.getAll() : [];
    const normalized = (Array.isArray(cached) ? cached : []).map(normalizeFarmerRow);
    if (normalized.length) setRows(normalized);
    loadFromServer();
  }, [customersRepo, loadFromServer]);

  // ---------------- list derived ----------------
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const hay = `${r.kno} ${r.cname} ${r.enname} ${r.mobno} ${r.status} ${r.type}`.toLowerCase();
      return hay.includes(s);
    });
  }, [rows, q]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / pageSize)),
    [filtered.length, pageSize]
  );

  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const paged = filtered.slice(start, start + pageSize);

  const showingFrom = filtered.length === 0 ? 0 : start + 1;
  const showingTo = Math.min(start + paged.length, filtered.length);

  useEffect(() => setPage(1), [q, pageSize]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // ---------------- form helpers ----------------
  function snapshotInitialForm() {
    initialFormRef.current = {
      fCode,
      fFirst,
      fLast,
      fEn,
      fPhone,
      fMilkType,
      fStatus,
      extraItems: JSON.parse(JSON.stringify(extraItems || [])),
    };
  }

  function restoreInitialForm() {
    const s = initialFormRef.current;
    if (!s) return;
    setFCode(s.fCode);
    setFFirst(s.fFirst);
    setFLast(s.fLast);
    setFEn(s.fEn);
    setFPhone(s.fPhone);
    setFMilkType(s.fMilkType);
    setFStatus(s.fStatus);
    setExtraItems(s.extraItems || []);
  }

  function resetFormToBlank() {
    setFCode("");
    setFFirst("");
    setFLast("");
    setFEn("");
    setFPhone("");
    setFMilkType("");
    setFStatus("Active");
    setExtraItems([]);
  }

  const openAdd = () => {
    setSelectedRow(null);
    setEditMode(false);
    resetFormToBlank();
    setView("add");
    setTimeout(() => snapshotInitialForm(), 0);
  };

  const openDetails = (row) => {
    setSelectedRow(row);
    setEditMode(false);

    const raw = row?.raw || {};
    const nm = splitName(row.cname);

    setFCode(row.kno);
    setFFirst(normStr(raw.fname ?? raw.first_name ?? nm.first));
    setFLast(normStr(raw.lname ?? raw.last_name ?? nm.last));
    setFEn(normStr(raw.enname ?? raw.name_english ?? row.enname));
    setFPhone(normStr(raw.mobno ?? raw.phone ?? row.mobno));

    // ✅ FIX: strong normalize
    const typeVal = normalizeMilkType(raw.type ?? raw.milk_type ?? raw.milktype ?? row.type);
    setFMilkType(typeVal);

    setFStatus(statusLabel(raw));

    // ✅ FIX: dynamic extras from kapat/dey lists
    const extras = normalizeExtraItems(raw);
    setExtraItems(extras);

    setView("details");
    setTimeout(() => snapshotInitialForm(), 0);
  };

  const goBackToList = () => {
    setView("list");
    setEditMode(false);
    setSelectedRow(null);
  };

  // ---------------- save (base customer) ----------------
  function validateForm(isEdit) {
    const code = normStr(fCode);
    const first = normStr(fFirst);
    const last = normStr(fLast);
    const milk = normStr(fMilkType);
    const st = normStr(fStatus);

    if (!code) return "Enter Farmer Code";
    if (!first) return "Enter First Name";
    if (!last) return "Enter Last Name";
    if (!milk || milk === "Select Milk Type") return "Select Milk Type";
    if (!st) return "Select Status";

    if (!isEdit) {
      const dup = rows.some((r) => normStr(r.kno) === code);
      if (dup) return "Farmer Code already exists.";
    }
    return "";
  }

  async function saveFarmer() {
  const isEdit = view === "details" && editMode;

  const msg = validateForm(isEdit);
  if (msg) {
    alert(msg);
    return;
  }
  if (!mainuser) {
    alert("Session missing (mainuser). Please login again.");
    return;
  }

  try {
    setLoading(true);
    setErr("");

    // cname must be non-empty in PHP
    const cnameCombined = `${normStr(fFirst)} ${normStr(fLast)}`.trim();

    // type must be non-empty in PHP
    const typeSend = normalizeMilkType(fMilkType);

    // IMPORTANT: PHP expects these exact POST keys
    const params = new URLSearchParams();

    params.set("flag", isEdit ? "edit" : "new");                 // ✅ PHP uses $flag
    params.set("mainuser", String(mainuser));

    params.set("knoedit", isEdit ? normStr(selectedRow?.kno) : ""); // ✅ PHP uses $knoedit
    params.set("kno", normStr(fCode));
    params.set("cname", cnameCombined);
    params.set("enname", normStr(fEn));
    params.set("type", typeSend);
    params.set("mobno", normStr(fPhone));

    // ---- fields required by PHP file (send safe defaults) ----
    params.set("address", normStr(selectedRow?.raw?.address) || "-");

    // in EDIT mode PHP does: pass='$passedit'
    // so we must send pass to avoid blanking it
    params.set("pass", normStr(selectedRow?.raw?.pass) || "-");

    params.set("bname", normStr(selectedRow?.raw?.bankname) || "-");
    params.set("branch", normStr(selectedRow?.raw?.branch) || "-");
    params.set("accno", normStr(selectedRow?.raw?.accno) || "-");

    params.set("obal", normStr(selectedRow?.raw?.obal) || "0");
    params.set("othev", normStr(selectedRow?.raw?.othev) || "0");

    params.set("aadhar", normStr(selectedRow?.raw?.aadhar) || "-");
    params.set("farmerid", normStr(selectedRow?.raw?.farmerid) || "-");
    params.set("tag", normStr(selectedRow?.raw?.tag) || "-");

    params.set("mhvah", normStr(selectedRow?.raw?.mhvah) || "0");
    params.set("gayvah", normStr(selectedRow?.raw?.gayvah) || "0");
    params.set("mhthev", normStr(selectedRow?.raw?.mhthev) || "0");
    params.set("gaythev", normStr(selectedRow?.raw?.gaythev) || "0");

    params.set("kapatbill", normStr(selectedRow?.raw?.kapatbill) || "0");
    params.set("hptabill", normStr(selectedRow?.raw?.hptabill) || "0");
    params.set("ikapatliter", normStr(selectedRow?.raw?.ikapatliter) || "0");
    params.set("ikapatbill", normStr(selectedRow?.raw?.ikapatbill) || "0");

    // default matches your PHP fallback
    params.set("vibhag", normStr(selectedRow?.raw?.vibhag) || "दरपत्रक विभाग-१");

    const resp = await http.post(API_SAVE_CUSTOMER, params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      validateStatus: (s) => s >= 200 && s < 300,
    });

    const text =
      typeof resp.data === "string" ? resp.data.trim() : JSON.stringify(resp.data);

    if (/Number Exists/i.test(text)) {
      alert("Number Exists");
      return;
    }

    const ok = /Successfully saved\./i.test(text) || /Successfully Updated\./i.test(text);
    if (!ok) {
      alert(text || "Save failed");
      return;
    }

    // success -> back to list + reload
    setView("list");
    setEditMode(false);
    setSelectedRow(null);
    await loadFromServer();
  } catch (e) {
    const httpStatus = e?.response?.status;
    const server = e?.response?.data;
    if (httpStatus) {
      alert(
        `Save failed (HTTP ${httpStatus}).\nServer: ${
          typeof server === "string" ? server : JSON.stringify(server)
        }`
      );
    } else {
      alert(e?.message || "Network error");
    }
  } finally {
    setLoading(false);
  }
}

  // ---------------- UI components ----------------
  const TopBar = ({ title, onBack, right }) => (
    <div className="fmTopbar">
      <button className="fmBack" type="button" onClick={onBack} title="Back">
        ←
      </button>
      <div className="fmTopTitle">{title}</div>
      <div className="fmTopRight">{right}</div>
    </div>
  );

  const SectionTitle = ({ children }) => (
    <div className="fmSectionTitle">{children}</div>
  );

  const milkOptions = useMemo(() => {
    const base = ["", "Cow", "Buffalo", "Cow + Buffalo"];
    const cur = normStr(fMilkType);
    // ✅ FIX: ensure current value is visible even if backend sends different string
    if (cur && !base.includes(cur)) return [base[0], cur, "Cow", "Buffalo", "Cow + Buffalo"];
    return base;
  }, [fMilkType]);

  // ========================= ADD / DETAILS SCREEN =========================
  if (view === "add" || view === "details") {
    const isDetails = view === "details";
    const canEditFields = view === "add" || (view === "details" && editMode);

    return (
      <div className="fmPage">
        <TopBar
          title={view === "add" ? "Add Farmer" : "Farmer Details"}
          onBack={goBackToList}
          right={
            isDetails ? (
              <button
                className="fmEditBtn"
                type="button"
                onClick={() => {
                  if (!editMode) {
                    setEditMode(true);
                    snapshotInitialForm();
                  } else {
                    setEditMode(false);
                    restoreInitialForm();
                  }
                }}
                title="Edit"
              >
                ✎ <span style={{ marginLeft: 6 }}>{editMode ? "Cancel" : "Edit"}</span>
              </button>
            ) : null
          }
        />

        <div className="fmCard">
          <SectionTitle>Personal Details</SectionTitle>

          <div className="fmGrid">
            <div className="fmRow full">
              <label className="fmLabel">
                Farmer Code : <span className="fmReq">*</span>
              </label>
              <input
                className="fmInput"
                value={fCode}
                onChange={(e) => setFCode(e.target.value)}
                readOnly={isDetails} // locked in details/edit
              />
            </div>

            <div className="fmRow">
              <label className="fmLabel">
                First Name : <span className="fmReq">*</span>
              </label>
              <input
                className="fmInput"
                value={fFirst}
                onChange={(e) => setFFirst(e.target.value)}
                readOnly={!canEditFields}
              />
            </div>

            <div className="fmRow">
              <label className="fmLabel">
                Last Name : <span className="fmReq">*</span>
              </label>
              <input
                className="fmInput"
                value={fLast}
                onChange={(e) => setFLast(e.target.value)}
                readOnly={!canEditFields}
              />
            </div>

            <div className="fmRow">
              <label className="fmLabel">Name (English) :</label>
              <input
                className="fmInput"
                value={fEn}
                onChange={(e) => setFEn(e.target.value)}
                readOnly={!canEditFields}
              />
            </div>

            <div className="fmRow">
              <label className="fmLabel">Phone Number :</label>
              <input
                className="fmInput"
                value={fPhone}
                onChange={(e) => setFPhone(e.target.value)}
                readOnly={!canEditFields}
              />
            </div>
          </div>

          <div className="fmDivider" />

          <a className="fmLinkTitle" href="#milk" onClick={(e) => e.preventDefault()}>
            Milk Details
          </a>

          <div className="fmGrid" id="milk">
            <div className="fmRow full">
              <label className="fmLabel">
                Milk Type : <span className="fmReq">*</span>
              </label>

              {/* ✅ FIX: dropdown always shows the current value (even if unknown) */}
              <select
                className="fmSelect"
                value={fMilkType}
                onChange={(e) => setFMilkType(e.target.value)}
                disabled={!canEditFields}
              >
                <option value="">{view === "add" ? "Select Milk Type" : "Select Milk Type"}</option>
                {milkOptions
                  .filter((x) => x !== "")
                  .map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
              </select>
            </div>

            <div className="fmRow full">
              <label className="fmLabel">
                Status : <span className="fmReq">*</span>
              </label>
              <select
                className="fmSelect"
                value={fStatus}
                onChange={(e) => setFStatus(e.target.value)}
                disabled={!canEditFields}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* ✅ FIX: Extra Information from kapat/dey list (NOT hard-coded) */}
          {view === "details" && editMode ? (
            <>
              <div className="fmDivider" />
              <SectionTitle>Extra Information</SectionTitle>

              {extraItems.filter((x) => !x._hdr).length === 0 ? (
                <div style={{ padding: "10px 0", color: "#6b7280" }}>
                  No kapat/dey info received from server for this customer.
                </div>
              ) : (
                <div className="fmGrid">
                  {extraItems
                    .filter((x) => !x._hdr)
                    .map((it) => (
                      <div className="fmRow full" key={`${it.kind}-${it.sr}-${it.label}`}>
                        <label className="fmLabel">{it.label} :</label>
                        <input
                          className="fmInput"
                          value={normStr(it.amt)}
                          onChange={(e) => {
                            const v = e.target.value;
                            setExtraItems((prev) =>
                              prev.map((p) =>
                                p.kind === it.kind && p.sr === it.sr ? { ...p, amt: v } : p
                              )
                            );
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    ))}
                </div>
              )}
            </>
          ) : null}

          <div className="fmActions">
            <button
              type="button"
              className="fmBtnGhost"
              onClick={() => {
                if (view === "add") resetFormToBlank();
                else restoreInitialForm();
              }}
              disabled={loading}
            >
              Reset
            </button>

            {(view === "add" || (view === "details" && editMode)) && (
              <button
                type="button"
                className="fmBtnPrimary"
                onClick={saveFarmer}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            )}
          </div>

          {err ? <div className="farmersError" style={{ marginTop: 10 }}>{err}</div> : null}
        </div>
      </div>
    );
  }

  // ========================= LIST VIEW =========================
  return (
    <div className="farmersPage">
      <div className="farmersHeader">
        <div className="farmersTitle">Farmers</div>
        <button className="farmersAddBtn" type="button" onClick={openAdd}>
          + Add Farmer
        </button>
      </div>

      <div className="farmersStatsRow">
        <div className="farmersStatsLeft">
          <div className="farmersSectionTitle">Farmers</div>
        </div>

        <div className="farmersSearchBox">
          <div className="farmersSearchLabel">Search</div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="farmersSearchInput"
          />
        </div>
      </div>

      <div className="farmersCard">
        {err ? <div className="farmersError">{err}</div> : null}

        <div className="farmersTableWrap">
          <table className="farmersTable">
            <thead>
              <tr>
                <th>Farmer Code</th>
                <th>Farmer Name</th>
                <th>Name English</th>
                <th>Status</th>
                <th>Phone Number</th>
                <th className="farmersActionsCol">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="farmersLoading">
                    Loading...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={6} className="farmersEmpty">
                    No records
                  </td>
                </tr>
              ) : (
                paged.map((r) => (
                  <tr key={`${r.kno}-${r.mobno}-${r.cname}`}>
                    <td>{r.kno}</td>
                    <td>{r.cname}</td>
                    <td>{r.enname}</td>
                    <td className={r.status.toLowerCase() === "active" ? "stActive" : "stInactive"}>
                      {r.status}
                    </td>
                    <td>{r.mobno}</td>
                    <td className="farmersActionsCol">
                      <button
                        className="farmersEyeBtn"
                        type="button"
                        onClick={() => openDetails(r)}
                        title="Open"
                      >
                        👁
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="farmersFooter">
          <div className="farmersFooterLeft">
            Showing {showingFrom} to {showingTo} of {filtered.length} entries
          </div>

          <div className="farmersFooterRight">
            <button className="pgBtn" type="button" onClick={() => setPage(1)} disabled={safePage <= 1}>
              «
            </button>
            <button
              className="pgBtn"
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              ‹
            </button>

            <span className="pgNum">{safePage}</span>

            <button
              className="pgBtn"
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              ›
            </button>
            <button className="pgBtn" type="button" onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}>
              »
            </button>

            <select className="pgSize" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <button className="farmersReload" type="button" onClick={loadFromServer} disabled={loading}>
              Reload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
